// Internal primitive — DO NOT import from outside the hooks/ folder.
// Public consumers must use `useResourceImage` from `@/shared/lib/hooks/useResourceImage`.
import { useEffect, useMemo, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getOrCreateBlobUrl } from '@/shared/lib/blobUrlCache';
import {
  ResizeFit,
  ResizeFormat,
  computeResizeTarget,
  getResizedImage,
  isResizableMime,
  peekResizedImage,
} from '@/workers/imageResize.client';

export type { ResizeFit, ResizeFormat };

// Re-exported for the many modules that hold a Blob (ImagesLightbox, ArtworkDnd,
// ImagesModalVersioning, …) and import the shared blob-URL cache from here.
export { getOrCreateBlobUrl };

export const CACHE_TIME_MS = 30 * 60 * 1000;

// Full-resolution source blobs are only needed long-term by full-size viewers
// (lightbox). For table thumbnails/previews the source is downloaded only to be
// resized once, so it can be released soon after the cell unmounts — this keeps
// memory bounded when scrolling through image-heavy grids.
const THUMBNAIL_SOURCE_CACHE_TIME_MS = 60 * 1000;

// ---------------------------------------------------------------------------
// Image size tiers
// ---------------------------------------------------------------------------

export type ImageTier = 'avatar' | 'thumb' | 'preview' | 'card' | 'full';

export const IMAGE_TIER_SIZE: Record<Exclude<ImageTier, 'full'>, number> = {
  avatar: 40,
  thumb: 80,
  preview: 240,
  card: 480,
};

export type ImageSizeSpec =
  | { tier: ImageTier }
  | { width: number; height?: number }
  | { height: number; width?: number };

// ---------------------------------------------------------------------------
// useCachedImage primitive
// ---------------------------------------------------------------------------

export interface UseCachedImageOptions {
  objectId: string | number | null | undefined;
  size?: ImageSizeSpec;
  fetcher: (id: string | number, signal?: AbortSignal) => Promise<Blob>;
  queryKeyPrefix: readonly unknown[];
  format?: ResizeFormat;
  fit?: ResizeFit;
  enabled?: boolean;
}

export interface UseImageResult {
  url: string | null;
  sourceUrl: string | null;
  isLoading: boolean;
  isFetching: boolean;
  isResizing: boolean;
  error: Error | null;
  retry: () => void;
}

const isFullTier = (size: ImageSizeSpec): boolean => 'tier' in size && size.tier === 'full';

const resolveLogicalSize = (size: ImageSizeSpec): { width: number; height?: number } => {
  if ('tier' in size) {
    const edge = IMAGE_TIER_SIZE[size.tier as Exclude<ImageTier, 'full'>];
    return { width: edge, height: edge };
  }
  if ('width' in size && size.width != null) {
    return { width: size.width, height: size.height };
  }
  return { width: size.height!, height: size.height };
};

const sizeSpecToKey = (size: ImageSizeSpec): string => {
  if ('tier' in size) return `t:${size.tier}`;
  return `wh:${'width' in size ? (size.width ?? '') : ''}x${'height' in size ? (size.height ?? '') : ''}`;
};

const prefixToKey = (prefix: readonly unknown[]): string => prefix.map((p) => String(p)).join('|');

export const useCachedImage = (opts: UseCachedImageOptions): UseImageResult => {
  const {
    objectId,
    size = { tier: 'full' },
    fetcher,
    queryKeyPrefix,
    format = 'auto',
    fit = 'cover',
    enabled = true,
  } = opts;

  const prefix = useMemo<readonly unknown[]>(() => queryKeyPrefix, [queryKeyPrefix]);
  const isQueryEnabled = enabled && objectId != null && objectId !== '';
  const isFull = isFullTier(size);

  // Cache key for the resized output, computed independently of the source blob
  // so we can look up an already-resized thumbnail before fetching (or even
  // deciding to fetch) the full-resolution source.
  const resizeCacheKey = useMemo(() => {
    if (isFull || objectId == null || objectId === '') return '';
    const target = computeResizeTarget(
      resolveLogicalSize(size).width,
      resolveLogicalSize(size).height,
    );
    return [
      prefixToKey(prefix),
      String(objectId),
      sizeSpecToKey(size),
      target.dpr,
      format,
      fit,
    ].join('|');
  }, [isFull, prefix, objectId, size, format, fit]);

  // Cache-first: if the resized thumbnail is already in memory, paint it
  // immediately and skip the expensive full-resolution source download entirely.
  const cachedResizedUrl = resizeCacheKey ? (peekResizedImage(resizeCacheKey)?.url ?? null) : null;

  const sourceQueryKey = useMemo(
    () => (isQueryEnabled ? [...prefix, String(objectId)] : [...prefix, 'disabled']),
    [prefix, objectId, isQueryEnabled],
  );

  const queryClient = useQueryClient();

  const {
    data: rawSourceBlob,
    isLoading: isFetching,
    isError: isFetchError,
    error: fetchError,
    refetch,
  } = useQuery({
    queryKey: sourceQueryKey,
    queryFn: ({ signal }) => fetcher(objectId!, signal),
    enabled: isQueryEnabled && (isFull || !cachedResizedUrl),
    staleTime: Infinity,
    cacheTime: isFull ? CACHE_TIME_MS : THUMBNAIL_SOURCE_CACHE_TIME_MS,
    retry: 1,
  });

  // Guard against corrupt cached values — e.g. a Blob that was persisted to
  // localStorage and rehydrated as an empty object. Treat anything that isn't
  // a real Blob as missing data and refetch so a valid Blob replaces it.
  const sourceBlob = rawSourceBlob instanceof Blob ? rawSourceBlob : undefined;

  useEffect(() => {
    if (rawSourceBlob != null && !(rawSourceBlob instanceof Blob)) {
      queryClient.removeQueries({ queryKey: sourceQueryKey });
      refetch();
    }
  }, [rawSourceBlob, queryClient, sourceQueryKey, refetch]);

  const sourceUrl = useMemo(
    () => (sourceBlob ? getOrCreateBlobUrl(sourceBlob) : null),
    [sourceBlob],
  );

  const wantsResize = !!sourceBlob && !isFull && isResizableMime(sourceBlob.type);

  const [resizedUrl, setResizedUrl] = useState<string | null>(null);
  const [resizeError, setResizeError] = useState<Error | null>(null);

  const sizeRef = useRef(size);
  const formatRef = useRef(format);
  const fitRef = useRef(fit);
  sizeRef.current = size;
  formatRef.current = format;
  fitRef.current = fit;

  useEffect(() => {
    if (!wantsResize || !resizeCacheKey || !sourceBlob) {
      setResizedUrl(null);
      setResizeError(null);
      return;
    }

    let cancelled = false;
    const target = computeResizeTarget(
      resolveLogicalSize(sizeRef.current).width,
      resolveLogicalSize(sizeRef.current).height,
    );

    getResizedImage({
      cacheKey: resizeCacheKey,
      sourceBlob,
      targetWidth: target.width,
      targetHeight: target.height,
      fit: fitRef.current,
      format: formatRef.current,
    })
      .then((res) => {
        if (cancelled) return;
        setResizedUrl(res.url);
        setResizeError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setResizeError(err instanceof Error ? err : new Error(String(err)));
        setResizedUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [resizeCacheKey, sourceBlob, wantsResize]);

  const isResizing = wantsResize && !!resizeCacheKey && resizedUrl === null && resizeError === null;

  useEffect(() => {
    if (!sourceUrl) return;
    const img = new Image();
    img.src = sourceUrl;
    img.decode().catch(() => {
      /* paint-time decode is the correct fallback */
    });
  }, [sourceUrl]);

  const url = isFull ? sourceUrl : (cachedResizedUrl ?? (wantsResize ? resizedUrl : sourceUrl));
  const error =
    (fetchError as Error | null) ??
    resizeError ??
    (isFetchError ? new Error('image fetch failed') : null);

  return {
    url,
    sourceUrl,
    isLoading: cachedResizedUrl ? false : isFetching || isResizing,
    isFetching,
    isResizing,
    error,
    retry: () => {
      refetch();
    },
  };
};
